<template>
  <div class="tw:flex tw:flex-col tw:gap-4 tw:text-gray-800">
    <div class="tw:flex tw:flex-col tw:gap-3 tw:md:flex-row tw:md:items-center tw:md:justify-between">
      <p class="tw:text-sm tw:text-gray-700">
        {{
          isAlreadyInOrganisation
            ? 'You are already part of this organisation as support.'
            : 'Add yourself to this organisation as support.'
        }}
      </p>
      <MainButton size="medium" @click="joinOrganisation" :disabled="isJoining || isAlreadyInOrganisation">
        <span v-if="isJoining" class="tw:min-w-[143px]"><SmallDotLoader color="#fff" /></span>
        <span v-else>{{ isAlreadyInOrganisation ? 'Already in organisation' : 'Join as support' }}</span>
      </MainButton>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'

import MainButton from '@/components/base/buttons/MainButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import useToastNotifications from '@/components/notifications/useToastNotifications'
import { useJoinOrganisationInAdminTool } from '@/components/adminTool/queries'
import { useSessionValues } from '@/composables/useSessionValues'

const props = defineProps({
  organisationId: String,
  users: {
    type: Array,
    default: () => [],
  },
})

const emit = defineEmits(['fetchData'])
const fetchData = () => emit('fetchData')

const store = useStore()
const { displayNotification } = useToastNotifications()
const { isPending: isJoining, joinOrganisationAsSupport } = useJoinOrganisationInAdminTool()
const { userId } = useSessionValues()
const hasJoinedCurrentOrganisation = ref(false)

const isExistingOrganisationUser = computed(() => {
  return props.users.some(user => user?.id === userId.value)
})
const isAlreadyInOrganisation = computed(() => {
  return isExistingOrganisationUser.value || hasJoinedCurrentOrganisation.value
})

watch(
  () => props.organisationId,
  () => {
    hasJoinedCurrentOrganisation.value = false
  }
)

const joinOrganisation = () => {
  if (!props.organisationId || isAlreadyInOrganisation.value || isJoining.value) return

  joinOrganisationAsSupport(
    {
      organisationId: props.organisationId,
    },
    {
      onSuccess: async () => {
        hasJoinedCurrentOrganisation.value = true
        await store.dispatch('session/getOrganisationsList')
        displayNotification({
          message: 'You have been added to the organisation.',
          isHtml: false,
          type: 'success',
        })
        fetchData()
      },
      onError: error => {
        displayNotification({
          message: error.response?.data?.message ?? 'Something went wrong while joining the organisation.',
          isHtml: false,
          type: 'error',
        })
      },
    }
  )
}
</script>
